import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar sesión al cargar
  useEffect(() => {
    verificarSesion();
  }, []);

  const verificarSesion = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('auth_token');

      if (storedUser && token) {
        // Verificar que el token sigue siendo válido
        const response = await authApi.verificarSesion();
        if (response && response.success) {
          setUser(response.usuario);
          localStorage.setItem('user', JSON.stringify(response.usuario));
        } else {
          // Token inválido, limpiar
          localStorage.removeItem('user');
          localStorage.removeItem('auth_token');
          setUser(null);
        }
      }
    } catch (err) {
      console.error('Error verificando sesión:', err);
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (usuario, password) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authApi.login(usuario, password);
      if (response.success) {
        setUser(response.usuario);
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('user', JSON.stringify(response.usuario));
        return { success: true };
      } else {
        setError(response.message || 'Error al iniciar sesión');
        return { success: false, message: response.message };
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Error de conexión';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Error en logout:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
    }
  };

  const cambiarPassword = async (passwordActual, passwordNuevo) => {
    try {
      const response = await authApi.cambiarPassword(passwordActual, passwordNuevo);
      return response;
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Error al cambiar contraseña' };
    }
  };

  // Verificar si el usuario tiene cierto rol
  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') {
      return user.rol === roles;
    }
    return roles.includes(user.rol);
  };

  // Verificar si es administrador
  const isAdmin = () => hasRole(['admin', 'Administrador']);

  // Verificar si es técnico
  const isTecnico = () => hasRole(['admin', 'Administrador', 'tecnico']);

  // Verificar si es recepcionista
  const isRecepcionista = () => hasRole(['admin', 'Administrador', 'recepcionista']);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    cambiarPassword,
    hasRole,
    isAdmin,
    isTecnico,
    isRecepcionista,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
